"use client";
import {useCallback,useEffect,useRef,useState,useSyncExternalStore} from 'react';
import {ArrowRight,Check,X} from 'lucide-react';
import {Dialog,DialogClose,DialogContent,DialogDescription,DialogTitle,DialogTrigger} from '@/components/ui/dialog';
import {api} from '@/lib/api';
import {formatBitePlanPrice} from '@/lib/bite-plan-price';
import {trackBiteEvent} from '@/lib/bite-measurement';
import {useBiteScreen} from './use-bite-screen';
import {requestBiteCheckout} from '@/lib/bite-checkout';
import {openBiteLogin} from '@/lib/bite-signup-referral';
import {biteGiftDismissal} from '@/lib/bite-gift-dismissal';
import {BITE_PRICE_OFFER_EVENT} from '@/lib/bite-price-offer';
import {BiteButton,BiteLinkButton} from './bite-ui';
import {BiteOfferGift} from './bite-offer-gift';
import {BiteCreditAmount} from './bite-credit';
import plans from '../../../../config/bite-plans.json';
import credits from '../../../../config/bite-credits.json';
import './bite-report-offer.css';
import './bite-offer-gift.css';
import './bite-price-offer.css';
type Quote={amount:number;regularAmount:number;discountPercent:number;eligible:boolean;loggedIn:boolean};
export function BiteReportOffer({ticker='',placement='report_offer',preview,hideGift=false}:{ticker?:string;placement?:'report_offer'|'pricing';preview?:Quote;hideGift?:boolean}){
 const landing=placement==='pricing';
 const root=useRef<HTMLElement>(null);
 const [contentMounted,setContentMounted]=useState(false);
 const attachContent=useCallback((node:HTMLElement|null)=>{root.current=node;setContentMounted(Boolean(node));},[]);
 const gift=useRef<HTMLDivElement>(null),loginAfterClose=useRef<string|null>(null);
 const [open,setOpen]=useState(false);
 const source=useRef<'gift'|'prices'>('gift');
 const [priceOffer,setPriceOffer]=useState(false);
 const [viewport,setViewport]=useState<{width:number;height:number;left:number;top:number}|null>(null);
 useEffect(()=>{
  if(!open)return;
  const visual=window.visualViewport;
  const update=()=>setViewport({width:visual?.width||window.innerWidth,height:visual?.height||window.innerHeight,left:visual?.offsetLeft||0,top:visual?.offsetTop||0});
  update();visual?.addEventListener('resize',update);visual?.addEventListener('scroll',update);window.addEventListener('resize',update);
  return()=>{visual?.removeEventListener('resize',update);visual?.removeEventListener('scroll',update);window.removeEventListener('resize',update);};
 },[open]);
 const dismissed=useSyncExternalStore(biteGiftDismissal.subscribe,biteGiftDismissal.getSnapshot,biteGiftDismissal.getServerSnapshot);
 const [tabActive,setTabActive]=useState(true);
 const [pricingVisible,setPricingVisible]=useState(false);
 const [flow]=useState(()=>{
  const params=typeof window==='undefined'?null:new URLSearchParams(window.location.search);
  const returned=params?.get('flow');
  return landing&&params?.get('offer')==='report_once'&&params.get('cta')==='pricing'&&/^[a-f0-9-]{36}$/.test(returned||'')?returned!:crypto.randomUUID();
 });
 const resumed=useRef(false);
 const [active,setActive]=useState(false);
 const [quote,setQuote]=useState<Quote|null>(preview||null),[busy,setBusy]=useState(false),[error,setError]=useState('');
 useEffect(()=>{
  const update=()=>setTabActive(!document.hidden);update();
  document.addEventListener('visibilitychange',update);
  return()=>document.removeEventListener('visibilitychange',update);
 },[]);
 useEffect(()=>{
  if(!open){setActive(false);return;}
  let visible=false;
  const update=()=>setActive(visible&&!document.hidden);
  const observer=new IntersectionObserver(([entry])=>{visible=entry.isIntersecting;update();},{threshold:0});
  if(root.current)observer.observe(root.current);
  document.addEventListener('visibilitychange',update);
  return()=>{observer.disconnect();document.removeEventListener('visibilitychange',update);};
 },[open,contentMounted]);
 useEffect(()=>{
  const controller=new AbortController();
  if(!preview)void api<Quote>('/api/bite/stripe/report-offer',{signal:controller.signal}).then(setQuote).catch(()=>{});
  return()=>controller.abort();
 },[preview]);
 const giftVisible=Boolean(quote?.eligible)&&(Boolean(preview)||!dismissed);
 useEffect(()=>{
  if(!landing||!giftVisible||!gift.current?.parentElement)return;
  const observer=new IntersectionObserver(([entry])=>setPricingVisible(entry.isIntersecting));
  observer.observe(gift.current.parentElement);
  return()=>observer.disconnect();
 },[landing,giftVisible]);
 useEffect(()=>{
  if(landing)return;
  const showPrices=()=>{
   source.current='prices';
   setPriceOffer(true);
   setOpen(true);
   if(!preview)try{trackBiteEvent('click',{label:'bite.report_prices.subscribe',report_offer:plans.reportOffer.id,flow,eligible:quote?.eligible===true});}catch{/* Measurement must not block the offer. */}
  };
  window.addEventListener(BITE_PRICE_OFFER_EVENT,showPrices);
  return()=>window.removeEventListener(BITE_PRICE_OFFER_EVENT,showPrices);
 },[flow,preview,quote,landing]);
 useBiteScreen(gift,!preview&&giftVisible&&!open,'report_gift_view',`${ticker}:${flow}`,{report_offer:plans.reportOffer.id,label:placement,flow});
 useBiteScreen(root,!preview&&Boolean(quote)&&open&&contentMounted,'report_offer_view',`${ticker}:${flow}`,{report_offer:quote?.eligible?plans.reportOffer.id:undefined,label:placement,flow});
 useEffect(()=>{
  if(!landing||preview||!quote?.loggedIn||resumed.current)return;
  const params=new URLSearchParams(window.location.search);
  if(params.get('offer')!=='report_once'||params.get('cta')!=='pricing'||params.get('plan')!=='monthly'||params.get('flow')!==flow)return;
  resumed.current=true;setOpen(true);
  const amount=Number(params.get('amount'));
  for(const key of ['plan','action','amount','offer','cta','flow'])params.delete(key);
  window.history.replaceState(window.history.state,'',`${window.location.pathname}?${params}${window.location.hash}`);
  if(quote.eligible&&amount===quote.amount)void subscribe(true);
  // Resume the explicit purchase once, using the original flow and server quote.
  // eslint-disable-next-line react-hooks/exhaustive-deps
 },[landing,preview,quote,flow]);
 function changeOpen(next:boolean){
  if(next){source.current='gift';setPriceOffer(false);}
  if(next!==open&&!preview)try{trackBiteEvent('click',{label:source.current==='prices'?'bite.report_prices.close':landing?(next?'bite.landing_gift.open':'bite.landing_gift.close'):next?'bite.report_gift.open':'bite.report_gift.close',report_offer:plans.reportOffer.id,flow,eligible:quote?.eligible===true});}catch{/* Closing remains available when measurement fails. */}
  if(open&&!next&&!preview&&!loginAfterClose.current)biteGiftDismissal.dismiss();
  setOpen(next);
 }
 const displayQuote=quote||plans.reportOffer;
 const won=formatBitePlanPrice;
 async function subscribe(resume=false){
  if(!quote||preview)return;
  setBusy(true);setError('');
  try{
   const result=await requestBiteCheckout({ticker,plan:'monthly',loggedIn:quote.loggedIn,expectedAmount:quote.amount,offer:'report_once',placement,flow,loginPath:landing?'/bite':undefined,resume});
   if(result.url){const url=new URL(result.url,window.location.origin);if(url.pathname==='/bite/login'){loginAfterClose.current=url.searchParams.get('next')||'/bite/pro';changeOpen(false);}else window.location.assign(result.url);}
   else setQuote({...quote,eligible:false});
  }catch(e){setError(e instanceof Error?e.message:'결제창에 연결하지 못했습니다. 다시 시도해주세요.');}
  finally{setBusy(false);}
 }
 return <Dialog open={open} onOpenChange={changeOpen}>
  {giftVisible&&!hideGift&&<div ref={gift} className={`bite-offer-floater${landing?' bite-offer-floater--pricing':preview?' bite-offer-floater--preview':''}`} data-open={open} data-active={tabActive&&(!landing||pricingVisible)} id={landing?'bite-landing-offer':'bite-report-offer'}>
   <DialogTrigger asChild><BiteButton type="button" variant="ghost" className="bite-offer-gift" data-no-track="true" aria-label="월간 패스 할인 혜택 보기"><BiteOfferGift/></BiteButton></DialogTrigger>
  </div>}
  {preview&&!landing&&<BiteButton variant="secondary" data-no-track="true" onClick={()=>{source.current='prices';setPriceOffer(true);setOpen(true);}}>가격 블러 팝업 보기</BiteButton>}
  <DialogContent style={viewport?{left:viewport.left+viewport.width/2,top:viewport.top+viewport.height/2,width:viewport.width-32,maxHeight:viewport.height-32,'--bite-offer-viewport-height':`${viewport.height}px`} as React.CSSProperties:undefined} className={`bite-scope bite-offer-dialog${priceOffer?' bite-offer-dialog--prices':''}`} overlayClassName="bite-scope bite-offer-overlay" showCloseButton={false} onCloseAutoFocus={event=>{if(loginAfterClose.current){event.preventDefault();const next=loginAfterClose.current;loginAfterClose.current=null;openBiteLogin(next);}}}>
  <DialogClose asChild><BiteButton type="button" variant="ghost" className="bite-offer-dialog__close" aria-label="구독 혜택 닫기" data-no-track="true"><X aria-hidden="true"/></BiteButton></DialogClose>
  <section ref={attachContent} className="bite-report-offer" data-animate={active} aria-label="월간 패스 첫 구독 혜택">
  <div className="bite-report-offer__story">
   <div className="bite-report-offer__brand"><b>Bite</b><span>월간 패스</span></div>
   {!priceOffer&&<DialogTitle className="bite-report-offer__headline">{(!quote||quote.eligible)?<><span>이 버튼에서만,</span><strong>{displayQuote.discountPercent}% 할인</strong></>:<span>Bite 월간 패스</span>}</DialogTitle>}
   {priceOffer?<DialogTitle className="bite-report-offer__benefit-title">내 종목의 <span className="bite-report-offer__price-term">목표가</span>와 <span className="bite-report-offer__price-term">익절가</span>,<br/><span className="bite-report-offer__price-term">손절가</span>까지 확인하세요</DialogTitle>:<h3 className="bite-report-offer__benefit-title">내 종목의 <span className="bite-report-offer__price-term">목표가</span>와 <span className="bite-report-offer__price-term">익절가</span>,<br/><span className="bite-report-offer__price-term">손절가</span>까지 확인하세요</h3>}
   <ul><li><Check aria-hidden="true"/><span>매월 <BiteCreditAmount amount={credits.monthly}/>을 받습니다.</span></li><li><Check aria-hidden="true"/><span>보고서 한 건을 <BiteCreditAmount amount={credits.report}/>으로 소장합니다.</span></li><li><Check aria-hidden="true"/><span>국내와 미국의 관심 종목을 분석합니다.</span></li></ul>
  </div>
  <div className="bite-report-offer__purchase">
   {priceOffer&&(!quote||quote.eligible)&&<p className="bite-price-offer__discount">이 버튼에서만, {displayQuote.discountPercent}% 할인</p>}
   {(!quote||quote.eligible)&&<p className="bite-report-offer__price"><del>{won(displayQuote.regularAmount)}원</del><span><strong>{won(displayQuote.amount)}</strong>원<small>/월</small></span></p>}
   {quote&&!quote.eligible?<BiteLinkButton href={source.current==='prices'?'/bite/pro':'/bite/me'} variant="inverse" size="lg" data-track={source.current==='prices'?'bite.report_prices.plans':'bite.report_offer.account'}>{source.current==='prices'?'구독하고 가격 확인하기':'구독 상태 확인하기'}<ArrowRight aria-hidden="true"/></BiteLinkButton>:<BiteButton variant="inverse" size="lg" data-track={landing?'bite.landing_offer.subscribe':'bite.report_offer.subscribe'} data-track-meta={JSON.stringify({report_offer:plans.reportOffer.id})} onClick={()=>void subscribe()} disabled={busy||!quote||Boolean(preview)}>{busy?'결제창 연결 중':`월 ${won(displayQuote.amount)}원에 구독하기`}<ArrowRight aria-hidden="true"/></BiteButton>}
   <DialogDescription className="bite-report-offer__terms">가입한 가격으로 매월 자동 결제됩니다.<br/>언제든 해지할 수 있습니다.</DialogDescription>
   {error&&<p role="alert" className="bite-report-offer__error">{error}</p>}
  </div>
 </section>
 </DialogContent>
 </Dialog>;
}
